import React, { FunctionComponent, useEffect, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { ISubscriptionOfferClientsClientData, useApi } from 'components/providers/ApiProvider'
import { useMessage } from 'components/elements/Message'
import { TextField } from 'components/elements/TextField'
import Drawer from '@material-ui/core/Drawer'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Theme } from '@material-ui/core'
import { Close, RemoveCircleOutline, Search } from '@material-ui/icons'
import IconButton from '@material-ui/core/IconButton'
import { SubmitButton } from 'Elements/SubmitButton'
import ListSkeleton from 'components/elements/ListSkeleton'
import ListLoadingIndicator from 'components/elements/ListLoadingIndicator'
import ListNoRecords from 'components/elements/ListNoRecords'
import GrantAccessForm from 'SubscriptionOffers/partials/GrantAccessForm'
import { useSubscriptionOffers } from 'SubscriptionOffers/SubscriptionOffersProvider'

export interface ClientsDrawerData {
  id: number
}

type Props = {
  data: ClientsDrawerData
}
type Functions = {}
type AllProps = Props & Functions

const useClasses = makeStyles((theme: Theme) => ({
  paper: {
    background: 'white',
    padding: '16px 24px',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '500px',
    },
  },
  filterButton: {
    minWidth: 'auto',
    '& .MuiButton-startIcon': {
      marginLeft: 0,
      marginRight: 0,
    },
  },
  revokeButtonCell: {
    padding: '0 16px 0 0',
  },
}))

const ClientsDrawer: FunctionComponent<AllProps> = ({ data }) => {
  if (!data) {
    return null
  }

  const classes = useClasses()
  const { t } = useTranslation()
  const api = useApi()
  const { setLoadList } = useSubscriptionOffers()
  const { showSuccess, showError } = useMessage()

  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState<{ client: string }>({
    client: '',
  })
  const [loading, setLoading] = useState(false)
  const [clients, setClients] = useState<ISubscriptionOfferClientsClientData[]>(null)
  const [modified, setModified] = useState(false)

  const load = () => {
    setLoading(true)

    api.getSubscriptionOfferClients({
      offer_id: data.id,
      client: filter.client || undefined,
    })
      .then(data => {
        setLoading(false)
        setClients(data.clients)
      })
      .catch(() => {
        setLoading(false)

        showError(`${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)
      })
  }

  useEffect(() => {
    setOpen(true)
  }, [data])

  useEffect(() => {
    if (!open) {
      return
    }

    load()
  }, [filter])

  const handleEnter = () => {
    load()
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleExited = () => {
    if (modified) {
      setLoadList()
      setModified(false)
    }

    formal.change('client', initialValues.client)
    formal.clearErrors()

    setFilter({
      client: '',
    })
    setClients(null)
  }

  const initialValues = {
    client: '',
  }

  const schema = yup.object().shape({
    client: yup.string().trim().matches(/^[0-9a-zA-Z]{0,30}$/),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      setFilter(schema.cast(values))
    },
  })

  const handleRevoke = (client: ISubscriptionOfferClientsClientData) => {
    if (!confirm(t(`confirms.revokeSubscriptionOfferAccess`))) {
      return
    }

    api.revokeSubscriptionOfferAccess({
      permission_id: client.permission_id,
    })
      .then(data => {
        if (!data || !data.status) {
          throw new Error
        }

        load()
        setModified(true)

        showSuccess(t('messages.subscriptionOfferAccessRevoked'))
      })
      .catch(error => {
        let message: string

        switch (error.code) {
          default:
            message = t('alerts.tryLater')
        }

        showError(`${t('alerts.couldNotRevokeSubscriptionOfferAccess')} ${message}`)
      })
  }

  return <>
    <Drawer anchor="right" open={open} onClose={handleClose} PaperProps={{ className: classes.paper }} SlideProps={{ onEnter: handleEnter, onExited: handleExited }}>
      <Box display="flex" justifyContent="flex-end" paddingBottom={2}>
        <IconButton size="small" onClick={handleClose}>
          <Close />
        </IconButton>
      </Box>
      <GrantAccessForm data={{ id: data.id }} onSuccess={() => {
        load()
        setModified(true)
      }} />

      <form {...formal.getFormProps()}>
        <Grid container spacing={1} style={{ paddingBottom: 8 }}>
          <TextField
            GridProps={{ xs: true }}
            label={t('client')}
            size="small"
            {...formal.getFieldProps('client')}
          />
          <SubmitButton
            GridProps={{ xs: 'auto' }}
            buttonText=""
            startIcon={<Search />}
            fullWidth={false}
            disabled={loading}
            className={classes.filterButton}
          />
        </Grid>
      </form>

      {!clients && <ListSkeleton columns={1} />}

      {clients && loading && <ListLoadingIndicator />}

      {clients && !clients.length && (
        <ListNoRecords />
      )}

      {clients && Boolean(clients.length) && (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('client')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client, key) => {
                return (
                  <TableRow key={key} hover>
                    <TableCell>
                      {client.name || <em>{t('allClients')}</em>}
                    </TableCell>
                    <TableCell
                      align="right"
                      className={classes.revokeButtonCell}
                      onClick={() => handleRevoke(client)}
                    >
                      <IconButton size="small">
                        <RemoveCircleOutline />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Drawer>
  </>
}

export default ClientsDrawer
